/**
 * 簡訊邀請「中繼頁」(/sms-invite/open) 的純函式。
 *
 * 背景：NCC 2024-11 起商業簡訊內的網址必須是自有網域，`liff.line.me` 會被電信擋下
 * （三竹 portal 顯示「資料有錯誤」）。所以簡訊只能放自有網域，再由本頁把客戶帶進
 * `https://liff.line.me/{瓦斯行 liff_main_id}/sms-invite?…`，讓註冊在 LINE app 內、
 * 用瓦斯行自己的 LIFF/channel 完成。
 *
 * 流程：
 *   簡訊 → /sms-invite/open?liffId=X&phone=…&invite=…[&order_id][&src]（本頁，不初始化 LIFF）
 *        → liff.line.me/X/sms-invite?…（自動嘗試 + 按鈕）
 *        → LINE 開 endpoint（/?liffId=X&liff.state=/sms-invite?…）→ /sms-invite 落地頁
 *   落地頁路徑是 /sms-invite，不是 /sms-invite/open，不會迴圈。
 *
 * 迴圈保險（shouldAutoOpenLiff）：網址帶 liff.state、或從 liff.line.me 轉來、或本 session
 * 已對同一邀請碼自動跳過一次 → 不再自動跳，只顯示按鈕。
 */
const LIFF_URL_PREFIX = "https://liff.line.me";
const SMS_INVITE_PATH = "/sms-invite";
const AUTO_OPENED_KEY_PREFIX = "sms_invite_auto_opened:";

export interface SmsInviteOpenParams {
  /** 瓦斯行的 LIFF ID（supplier_line_settings.liff_main_id）；空字串＝簡訊沒帶，退回預設 LIFF */
  liffId: string;
  phone: string;
  invite: string;
  order_id?: string;
  src?: string;
}

const clean = (raw: string | null | undefined): string => (raw ?? "").trim();

export const isSmsInviteOpenMode = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => /^\/sms-invite\/open\/?$/.test(pathname);

/** 讀網址參數；phone 與 invite 皆需存在才算有效，否則 null。 */
export const parseSmsInviteOpenParams = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
): SmsInviteOpenParams | null => {
  const params = new URLSearchParams(search);
  const phone = clean(params.get("phone"));
  const invite = clean(params.get("invite"));
  if (!phone || !invite) return null;
  const result: SmsInviteOpenParams = { liffId: clean(params.get("liffId")), phone, invite };
  const orderId = clean(params.get("order_id"));
  if (orderId) result.order_id = orderId;
  const src = clean(params.get("src"));
  if (src) result.src = src;
  return result;
};

/** 落地頁 query（順序固定：phone → invite → order_id → src），與 consumer buildSmsInviteUrl 一致。 */
const buildLandingQuery = (p: SmsInviteOpenParams, withLiffId: boolean): string => {
  const q = new URLSearchParams();
  if (withLiffId && p.liffId) q.set("liffId", p.liffId);
  q.set("phone", p.phone);
  q.set("invite", p.invite);
  if (p.order_id) q.set("order_id", p.order_id);
  if (p.src) q.set("src", p.src);
  return q.toString();
};

/** `https://liff.line.me/{liffId}/sms-invite?…`；沒 liffId 組不出來 → null。 */
export const buildSmsInviteLiffUrl = (p: SmsInviteOpenParams): string | null => {
  if (!p.liffId) return null;
  return `${LIFF_URL_PREFIX}/${p.liffId}${SMS_INVITE_PATH}?${buildLandingQuery(p, false)}`;
};

/** 本站落地路徑 `/sms-invite?liffId=…&phone=…`；帶 liffId 讓 getRuntimeLiffId 用瓦斯行 LIFF 初始化。 */
export const buildSmsInviteBrowserPath = (p: SmsInviteOpenParams): string =>
  `${SMS_INVITE_PATH}?${buildLandingQuery(p, true)}`;

const autoOpenedKey = (invite: string): string => `${AUTO_OPENED_KEY_PREFIX}${invite}`;

export const markSmsInviteAutoOpened = (invite: string): void => {
  try {
    window.sessionStorage.setItem(autoOpenedKey(invite), "1");
  } catch {
    // 忽略：sessionStorage 不可用時只是少一道保險
  }
};

const wasAutoOpened = (invite: string): boolean => {
  try {
    return window.sessionStorage.getItem(autoOpenedKey(invite)) === "1";
  } catch {
    return false;
  }
};

/**
 * 是否要自動導向 liff.line.me。三種情況一律不自動跳、只留按鈕：
 *  1. 網址帶 `liff.state`（這是 LIFF 回跳，不該再出去）
 *  2. referrer 來自 liff.line.me（剛從 LIFF 轉回來）
 *  3. 本 session 已對同一邀請碼自動跳過一次
 */
export const shouldAutoOpenLiff = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
): boolean => {
  const params = new URLSearchParams(search);
  if (params.has("liff.state")) return false;
  if (/^https?:\/\/liff\.line\.me(\/|$)/i.test(referrer)) return false;
  const invite = clean(params.get("invite"));
  if (invite && wasAutoOpened(invite)) return false;
  return true;
};
